import { useState, type ReactNode } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { ChevronDown } from 'lucide-react'
import { cn } from '@/lib/utils'
import { activeLeaf, activeSubLeaf, type NavItem, type NavLeaf } from './nav-config'
import { useTranslation } from 'react-i18next'

export const SUBNAV_ITEM = 'flex h-9 items-center gap-2 rounded-md px-2 text-sm transition-colors'
export const SUBNAV_ACTIVE = 'bg-accent font-medium text-primary'
export const SUBNAV_IDLE = 'text-foreground/85 hover:bg-accent/60'

export const subnavItemClass = (active: boolean) =>
  cn(SUBNAV_ITEM, active ? SUBNAV_ACTIVE : SUBNAV_IDLE)

/** Cột menu phụ trong khung nội dung (nền xanh rất nhạt), theo mẫu SaleSmartly. */
export function SubnavPanel({ title, children }: { title: string; children: ReactNode }) {
  return (
    <aside className="hidden w-56 shrink-0 flex-col overflow-y-auto border-r bg-subnav px-3 pb-4 md:flex">
      <h2 className="flex h-14 shrink-0 items-center px-2 text-lg font-medium">{title}</h2>
      {children}
    </aside>
  )
}

/** Menu phụ của một mục menu chính có nhiều trang con (Khách hàng, Báo cáo…). */
export function SectionSubnav({ item }: { item: NavItem }) {
  const { pathname } = useLocation()
  const current = activeLeaf(item, pathname)
  return (
    <SubnavPanel title={item.label}>
      <nav className="space-y-0.5">
        {item.children?.map((c) =>
          c.children ? (
            <SubnavGroup key={c.to} leaf={c} active={c === current} pathname={pathname} />
          ) : (
            <Link
              key={c.to}
              to={c.to}
              aria-current={c === current ? 'page' : undefined}
              className={subnavItemClass(c === current)}
            >
              <span className="truncate">{c.label}</span>
            </Link>
          ),
        )}
      </nav>
    </SubnavPanel>
  )
}

/**
 * Trang cấp 2 có menu con cấp 3 (Đội ngũ › Nhân viên AI): bấm chữ → trang của nó, bấm mũi tên → gập / mở. Mở sẵn khi
 * đang ở một trang bên trong; chuyển vào nhóm từ chỗ khác thì tự mở lại.
 */
function SubnavGroup({ leaf, active, pathname }: { leaf: NavLeaf; active: boolean; pathname: string }) {
  const [open, setOpen] = useState(active)
  // Vừa vào nhóm (active đổi false → true) → mở lại; điều chỉnh ngay lúc render, không cần effect.
  const [wasActive, setWasActive] = useState(active)
  if (active !== wasActive) {
    setWasActive(active)
    if (active) setOpen(true)
  }
  const { t } = useTranslation('shell')
  const sub = activeSubLeaf(leaf, pathname)
  const listId = `subnav-${leaf.to.replace(/\W+/g, '-')}`
  return (
    <div>
      <div className={cn(SUBNAV_ITEM, 'gap-0 p-0', active ? 'font-medium text-primary' : SUBNAV_IDLE)}>
        <Link
          to={leaf.to}
          aria-current={active && !sub ? 'page' : undefined}
          className={cn('flex h-full min-w-0 flex-1 items-center rounded-md px-2', active && !sub ? 'bg-accent' : 'hover:bg-accent/60')}
        >
          <span className="truncate">{leaf.label}</span>
        </Link>
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
          aria-controls={listId}
          aria-label={open ? t('subnav.collapseAria', { label: leaf.label }) : t('subnav.expandAria', { label: leaf.label })}
          title={open ? t('subnav.collapse') : t('subnav.expand')}
          className="flex h-9 w-8 shrink-0 items-center justify-center rounded-md text-foreground/60 hover:bg-accent/60 hover:text-foreground"
        >
          <ChevronDown className={cn('h-4 w-4 transition-transform', !open && '-rotate-90')} />
        </button>
      </div>
      {open && (
        <div id={listId} className="ml-3 mt-0.5 space-y-0.5 border-l pl-2">
          {leaf.children!.map((g) => (
            <Link
              key={g.to}
              to={g.to}
              aria-current={g === sub ? 'page' : undefined}
              className={cn(subnavItemClass(g === sub), 'h-8')}
            >
              <span className="truncate">{g.label}</span>
            </Link>
          ))}
        </div>
      )}
    </div>
  )
}
